import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { AppBar } from './components/AppBar';
import { TabBar } from './components/TabBar';
import { NavDrawer } from './components/NavDrawer';
import type { DrawerContent } from './components/NavDrawer';
import { ProgrammeSidebar } from './components/ProgrammeSidebar';
import { ToastProvider, useToast } from './components/Toast';
import { UpdatePrompt } from './components/UpdatePrompt';
import { useIsDesktop } from './hooks/useIsDesktop';
import { HomeView } from './views/HomeView';
import { RegistrationDataView } from './views/RegistrationDataView';
import { CompetitionsView } from './views/CompetitionsView';
import { ClubsView } from './views/ClubsView';
import { AthletesView } from './views/AthletesView';
import { StarterView } from './views/StarterView';
import { EntriesView } from './views/EntriesView';
import { ProgrammeView } from './views/ProgrammeView';
import type { ProgrammeViewHandle } from './views/ProgrammeView';
import { FigurePicker as FigurePickerComponent } from './views/FigurePicker';
import type { FigurePickerHandle } from './views/FigurePicker';
import { ImportExportSheet, pickImportFile } from './views/ImportExportSheet';
import type { ImportExportStage } from './views/ImportExportSheet';
import { WeboImportSheet, pickWeboImportFile } from './views/WeboImportSheet';
import type { WeboImportStage } from './views/WeboImportSheet';
import type { LegalKind } from './views/LegalSheet';
import { useConsent } from './hooks/useConsent';
import { NAV } from './navConfig';
import { parseWorkspace, programmeLabel } from '../domain/workspace';
import { buildSharedProgramme, decodeProgrammeIds, looksLikeProgrammeShare } from '../domain/share';
import { catalogueKeyFor, loadCatalogue } from '../data/catalogue';
import { download, pickFile } from '../lib/download';
import { slug } from '../lib/format';
import { t } from '../i18n';
import { ageTierLabel, starterTypeLabel } from '../i18n/labels';
import type { WorkspaceStore } from '../domain/store';
import type { ViewId } from '../types';

/** Screen order the mobile swipe-left/right gesture steps through — the
 *  same order the hamburger drawer lists them in. Previously also drove a
 *  visible "Step N" header with prev/next buttons (removed after tester
 *  feedback that framing these as a linear wizard was misleading); the
 *  swipe gesture itself carries no such label and stays. */
const STEP_ORDER: readonly ViewId[] = NAV.map((n) => n.id);

// Lazy-loaded: unlike the six step views above (always mounted, hidden via
// CSS so their state survives switching between them — see CLAUDE.md), each
// of these only ever mounts once a user action opens it, so there's nothing
// to preserve by keeping its code in the main bundle from first paint.
const ExportSheet = lazy(() => import('./views/ExportSheet').then((m) => ({ default: m.ExportSheet })));
const SettingsSheet = lazy(() => import('./views/SettingsSheet').then((m) => ({ default: m.SettingsSheet })));
const AboutSheet = lazy(() => import('./views/AboutSheet').then((m) => ({ default: m.AboutSheet })));
const LegalSheet = lazy(() => import('./views/LegalSheet').then((m) => ({ default: m.LegalSheet })));
const ConsentSheet = lazy(() => import('./views/ConsentSheet').then((m) => ({ default: m.ConsentSheet })));

function AppInner({ store }: { store: WorkspaceStore }) {
  const toast = useToast();
  const isDesktop = useIsDesktop();
  const [current, setCurrent] = useState<ViewId>(() => {
    const fromHash = location.hash.replace('#', '') as ViewId;
    return NAV.some((n) => n.id === fromHash) ? fromHash : 'home';
  });
  const [drawerContent, setDrawerContent] = useState<DrawerContent | null>(null);
  const [ieStage, setIeStage] = useState<ImportExportStage>({ kind: 'closed' });
  const [weboStage, setWeboStage] = useState<WeboImportStage>({ kind: 'closed' });
  const [exportSheetCompId, setExportSheetCompId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState<LegalKind | null>(null);
  const [cookieSettingsOpen, setCookieSettingsOpen] = useState(false);
  const consentDecided = useConsent();
  const mainRef = useRef<HTMLElement>(null);

  // `<ProgrammeView>` (below) is opened imperatively from the nav drawer and
  // programme rail via this ref, the same pattern the figure picker uses —
  // `programmeHandleRef` forwards to whatever the mounted component's own
  // `useImperativeHandle` exposes, always called after mount since it's
  // only invoked from a later user interaction.
  const programmeHandleRef = useRef<ProgrammeViewHandle>(null);
  const pickerHandleRef = useRef<FigurePickerHandle>(null);

  function activate(id: ViewId): void {
    setCurrent(id);
    // Landing on Programm with nothing to show yet: go straight to the
    // creation form rather than the empty state's extra "Neues Programm" tap.
    if (id === 'programme' && store.workspace.programs.length === 0) {
      programmeHandleRef.current?.openNewProgrammeForm();
    }
    if (location.hash !== `#${id}`) history.replaceState(null, '', `#${id}`);
    window.scrollTo(0, 0);
  }

  function stepTo(dir: 'prev' | 'next'): void {
    const idx = STEP_ORDER.indexOf(current);
    const nextId = STEP_ORDER[dir === 'prev' ? idx - 1 : idx + 1];
    if (nextId) activate(nextId);
  }

  function onDrawerToggle(): void {
    setDrawerContent({ kind: 'views', items: NAV, current });
  }

  function onOpenProgrammeList(): void {
    setDrawerContent({ kind: 'programmes' });
  }

  function selectProgramme(id: string): void {
    activate('programme');
    programmeHandleRef.current?.selectProgramme(id);
  }

  function newProgramme(): void {
    activate('programme');
    programmeHandleRef.current?.openNewProgrammeForm();
  }

  async function handleSave(): Promise<void> {
    const workspace = store.workspace;
    toast(
      await download(`${slug(workspace.meta.club, 'kuer-arbeitsbereich')}.kuer.json`, JSON.stringify(workspace, null, 2), 'application/json'),
    );
  }

  async function handleOpen(): Promise<void> {
    const file = await pickFile('application/json,.json');
    if (!file) return;
    try {
      store.replace(parseWorkspace(JSON.parse(file.text)));
      toast(t('app.opened', { file: file.name }));
    } catch {
      toast(t('app.openFailed'));
    }
  }

  async function handleImportSelection(): Promise<void> {
    const stage = await pickImportFile(toast);
    if (stage) setIeStage(stage);
  }

  function handleExportSelection(): void {
    setIeStage({ kind: 'export-tree' });
  }

  async function handleWeboImport(): Promise<void> {
    const stage = await pickWeboImportFile(toast);
    if (stage) setWeboStage(stage);
  }

  const showProgrammeRail = isDesktop && current === 'programme';

  // The hamburger drawer only exists on mobile — force it closed crossing
  // into the desktop layout, mirroring the vanilla `desktopMQ` listener.
  useEffect(() => {
    if (isDesktop) setDrawerContent(null);
  }, [isDesktop]);

  // Auto-import a Programme shared via URL fragment (domain/share.ts) — e.g.
  // a coach opening a "#1:2:e:<data>" link from another device/coach. Runs
  // once on mount; a hash that isn't share-shaped — including this app's own
  // view-switch hashes like "#sportler" — is left completely untouched.
  useEffect(() => {
    const fragment = location.hash.slice(1);
    if (!looksLikeProgrammeShare(fragment)) return;

    void (async () => {
      try {
        const decoded = decodeProgrammeIds(fragment);
        const catalogue = await loadCatalogue(catalogueKeyFor(decoded.type));
        const { programme, skippedIds } = buildSharedProgramme(decoded, catalogue);
        store.update((p) => {
          p.programs.push(programme);
          p.activeProgramId = programme.id;
        });
        const name = programmeLabel(programme, starterTypeLabel(programme.type), ageTierLabel(programme.ageTier));
        toast(
          skippedIds.length > 0
            ? t('share.importedWithSkipped', { name, count: skippedIds.length })
            : t('share.imported', { name }),
        );
        // Land straight on the imported programme — a coach opening the link
        // shouldn't need an extra tap to actually see what they were sent.
        // `activate()` replaces the hash with "#programme" itself, so the
        // share fragment is gone from the URL the same way the plain strip
        // below handles the error case.
        activate('programme');
      } catch {
        toast(t('share.decodeError'));
        // Only strip the hash here — the success path already replaced it
        // via activate('programme') above, and clearing it again afterwards
        // would wipe out that "#programme" hash right after setting it.
        history.replaceState(null, '', location.pathname + location.search);
      }
    })();
    // Deliberately mount-once: re-running on a later `store`/`toast` change
    // would just re-check an already-cleared hash.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Swipe left/right to step through the mobile flow, mirroring the arrow
  // buttons. A touch starting on an exercise row is excluded too — those
  // are now @dnd-kit-sortable draggable on touch as well as desktop (see
  // ProgrammeView.tsx), and a horizontal drag there shouldn't also be read
  // as a step-navigation swipe.
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    const SWIPE_MIN_DX = 60;
    const SWIPE_MAX_OFF_AXIS = 50;
    const SWIPE_MAX_MS = 600;
    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartAt = 0;
    let tracking = false;

    function onTouchStart(ev: TouchEvent): void {
      const touch = ev.touches[0];
      const target = ev.target;
      tracking =
        !isDesktop &&
        ev.touches.length === 1 &&
        touch !== undefined &&
        !(target instanceof Element && target.closest('input, textarea, select, .exercise'));
      if (!tracking || !touch) return;
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      touchStartAt = Date.now();
    }

    function onTouchEnd(ev: TouchEvent): void {
      if (!tracking) return;
      tracking = false;
      const touch = ev.changedTouches[0];
      if (!touch || Date.now() - touchStartAt > SWIPE_MAX_MS) return;
      const dx = touch.clientX - touchStartX;
      const dy = touch.clientY - touchStartY;
      if (Math.abs(dx) < SWIPE_MIN_DX || Math.abs(dy) > SWIPE_MAX_OFF_AXIS) return;
      stepTo(dx < 0 ? 'next' : 'prev');
    }

    main.addEventListener('touchstart', onTouchStart, { passive: true });
    main.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      main.removeEventListener('touchstart', onTouchStart);
      main.removeEventListener('touchend', onTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, current]);

  return (
    <>
      <header
        className="sticky top-0 z-40 flex flex-wrap items-center gap-2 bg-primary px-3.5 pt-[calc(10px+env(safe-area-inset-top))] pb-2.5 text-primary-foreground"
        style={isDesktop ? { paddingLeft: 'calc(var(--nav-w) + 14px)' } : undefined}
      >
        <AppBar
          onDrawerToggle={onDrawerToggle}
          onHome={() => activate('home')}
          onOpen={() => void handleOpen()}
          onSave={() => void handleSave()}
          onImportSelection={() => void handleImportSelection()}
          onExportSelection={handleExportSelection}
          onWeboImport={() => void handleWeboImport()}
          onSettings={() => setSettingsOpen(true)}
          onAbout={() => setAboutOpen(true)}
          onLegal={setLegalOpen}
          onCookieSettings={() => setCookieSettingsOpen(true)}
        />
      </header>
      <TabBar
        current={current}
        onNav={activate}
        onSettings={() => setSettingsOpen(true)}
        onAbout={() => setAboutOpen(true)}
        onLegal={setLegalOpen}
        onCookieSettings={() => setCookieSettingsOpen(true)}
      />
      {showProgrammeRail ? (
        <ProgrammeSidebar store={store} onSelectProgramme={selectProgramme} onNewProgramme={newProgramme} />
      ) : null}
      <main
        id="main"
        ref={mainRef}
        className="mx-auto w-full flex-1"
        style={
          isDesktop
            ? { paddingLeft: `calc(var(--nav-w)${showProgrammeRail ? ' + var(--rail-w)' : ''})`, paddingBottom: 0 }
            : { paddingBottom: 'env(safe-area-inset-bottom)' }
        }
      >
        <HomeView
          hidden={current !== 'home'}
          onNavigate={activate}
          onOpenPrivacyPolicy={() => setLegalOpen('datenschutz')}
          onOpen={() => void handleOpen()}
          onSave={() => void handleSave()}
          onImportSelection={() => void handleImportSelection()}
          onExportSelection={handleExportSelection}
        />
        <RegistrationDataView store={store} hidden={current !== 'registrationData'} />
        <CompetitionsView store={store} hidden={current !== 'competitions'} />
        <ClubsView store={store} hidden={current !== 'clubs'} />
        <AthletesView store={store} hidden={current !== 'athletes'} />
        <StarterView store={store} hidden={current !== 'starter'} />
        <ProgrammeView ref={programmeHandleRef} store={store} picker={pickerHandleRef} hidden={current !== 'programme'} />
        <EntriesView
          store={store}
          hidden={current !== 'entries'}
          onExport={setExportSheetCompId}
          onImportSelection={() => void handleImportSelection()}
          onExportSelection={handleExportSelection}
          onWeboImport={() => void handleWeboImport()}
        />
      </main>
      <NavDrawer
        content={drawerContent}
        store={store}
        onSelectView={activate}
        onOpenProgrammeList={onOpenProgrammeList}
        onSelectProgramme={selectProgramme}
        onNewProgramme={newProgramme}
        onBack={onDrawerToggle}
        onClose={() => setDrawerContent(null)}
      />
      <FigurePickerComponent ref={pickerHandleRef} />
      <ImportExportSheet store={store} toast={toast} stage={ieStage} onClose={() => setIeStage({ kind: 'closed' })} />
      <WeboImportSheet store={store} toast={toast} stage={weboStage} onClose={() => setWeboStage({ kind: 'closed' })} />
      {exportSheetCompId ? (
        <Suspense fallback={null}>
          <ExportSheet store={store} compId={exportSheetCompId} goTo={activate} onClose={() => setExportSheetCompId(null)} />
        </Suspense>
      ) : null}
      {settingsOpen ? (
        <Suspense fallback={null}>
          <SettingsSheet onClose={() => setSettingsOpen(false)} />
        </Suspense>
      ) : null}
      {aboutOpen ? (
        <Suspense fallback={null}>
          <AboutSheet onClose={() => setAboutOpen(false)} />
        </Suspense>
      ) : null}
      {legalOpen ? (
        <Suspense fallback={null}>
          <LegalSheet kind={legalOpen} onClose={() => setLegalOpen(null)} />
        </Suspense>
      ) : null}
      {/* Undecided: shows unconditionally as a blocking first-run banner
          (dismissable=false). Decided: only shows when reopened from the
          "Cookie-Einstellungen" menu entry. Each of the 5 sheets above gets
          its own Suspense boundary (not one shared one) so one slow-loading
          chunk can't hide an unrelated, already-ready dialog. */}
      {!consentDecided || cookieSettingsOpen ? (
        <Suspense fallback={null}>
          <ConsentSheet
            dismissable={consentDecided}
            onClose={() => setCookieSettingsOpen(false)}
            onOpenPrivacyPolicy={() => setLegalOpen('datenschutz')}
          />
        </Suspense>
      ) : null}
    </>
  );
}

export function App({ store }: { store: WorkspaceStore }) {
  return (
    <ToastProvider>
      <AppInner store={store} />
      <UpdatePrompt />
    </ToastProvider>
  );
}
